/**
 * Sentry Configuration
 * 
 * Comprehensive error monitoring, performance tracking, session replay, and logging
 * for production-grade React application
 */

import * as Sentry from '@sentry/react';
import React from 'react';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Sentry Configuration Object
 * Customize these settings based on your needs
 */
const sentryConfig = {
  // Sentry DSN - Data Source Name (unique project identifier)
  dsn: 'https://defd0dbefab7a1799d0e8315d1691dac@o4510144287735808.ingest.us.sentry.io/4510144291799040',
  
  // Environment detection
  environment: process.env.NODE_ENV || 'development',
  
  // Release version - Critical for tracking issues by version
  // Format: project@version+build
  release: `maphra@${require('../../package.json').version}`,
  
  // Distribution - specific build/deployment identifier
  dist: process.env.REACT_APP_BUILD_ID || Date.now().toString(),
  
  // Do NOT send default PII (IP addresses, etc.) — this is a security product.
  sendDefaultPii: false,
  
  // Integrations for enhanced monitoring
  integrations: [
    // Browser performance monitoring
    Sentry.browserTracingIntegration({
      // Enable tracing for all routes
      enableInp: true,
      enableLongTask: true,
    }),
    
    // Session replay - records user sessions for debugging.
    // Privacy-hardened: this app renders scan targets/results and handles auth
    // tokens + passwords, so replays must NOT capture on-screen text, media, or
    // request/response bodies (login password, refresh JWTs) to Sentry.
    Sentry.replayIntegration({
      // Mask all text content for privacy
      maskAllText: true,
      // Block all media (images, videos) for privacy
      blockAllMedia: true,
      // Do NOT capture request/response bodies (they contain credentials/tokens)
      networkCaptureBodies: false,
      networkRequestHeaders: [],
      networkResponseHeaders: [],
    }),
    
    // React specific integration
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation: () => window.location,
      useNavigationType: () => 'POP',
      createRoutesFromChildren: () => [],
      matchRoutes: () => null,
    }),
    
    // Breadcrumbs integration for detailed context
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      sentry: true,
      xhr: true,
    }),
    
    // HTTP client integration for API monitoring
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [[400, 599]],
    }),
  ],
  
  // Performance Monitoring - Tracing
  // 1.0 = 100% of transactions are sent to Sentry
  // In production, you might want to reduce this to 0.1 (10%) or lower
  tracesSampleRate: isProduction ? 0.2 : 1.0,
  
  // Configure which URLs should be traced for distributed tracing
  tracePropagationTargets: [
    'localhost',
    /^http:\/\/localhost:5000/,
    /^https:\/\/yourserver\.io\/api/,
    /^\/api/,
    window.location.origin,
  ],
  
  // Session Replay Configuration
  // Sample rate for normal sessions (10% in production, 100% in dev)
  replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
  
  // Sample rate for sessions with errors (100% - always capture errors)
  replaysOnErrorSampleRate: 1.0,
  
  // Enable automatic logging integration
  enableLogs: true,
  
  // Session tracking for Crash Free Sessions/Users metrics
  autoSessionTracking: true,
  
  // Track sessions across page loads
  enableAutoSessionTracking: true,
  
  // Session sample rate (1.0 = 100%)
  sessionSampleRate: 1.0,
  
  // Ignore certain errors (common browser extensions, etc.)
  ignoreErrors: [
    // Browser extension errors
    'top.GLOBALS',
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    
    // Network errors that are expected
    'NetworkError',
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    'ERR_BLOCKED_BY_CLIENT',
    'ERR_ABORTED',
    
    // ResizeObserver loop errors (common and harmless)
    'ResizeObserver loop',
    
    // Script loading errors from ad blockers
    'SecurityError',
    
    // Cancelled requests
    'AbortError',
    'The operation was aborted',
    
    // Non-actionable errors
    'Non-Error promise rejection captured',
    'Object Not Found Matching Id',
    
    // Storage quota errors (handled by browser maintenance)
    'QuotaExceededError',
    'exceeded the quota',
  ],
  
  // Ignore errors from certain URLs
  denyUrls: [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    
    // Common CDNs that might have issues
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /doubleclick\.net/i,
    
    // Sentry itself (avoid recursion)
    /sentry\.io/i,
    /ingest\.us\.sentry\.io/i,
  ],
  
  // Only enable in production or staging
  enabled: true,
  
  // Attach stack trace to every message
  attachStacktrace: true,
  
  // Maximum breadcrumbs to keep
  maxBreadcrumbs: 100,
  
  // Normalize depths for serialization
  normalizeDepth: 5,
  
  // Enable user feedback widget
  // Users can provide feedback when errors occur
  enableUserFeedback: true,
  
  // Before send hook - modify or filter events before sending
  beforeSend(event, hint) {
    // Filter out events in development if needed
    if (isDevelopment) {
      console.log('[Sentry] Event captured:', event);
      console.log('[Sentry] Event hint:', hint);
    }
    
    // Don't send events from localhost in production
    if (isProduction && event.request?.url?.includes('localhost')) {
      return null;
    }
    
    // Add custom tags
    event.tags = {
      ...event.tags,
      browser: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
    };
    
    // Add custom context
    event.contexts = {
      ...event.contexts,
      app: {
        name: 'Maphra Security Platform',
        version: require('../../package.json').version,
        release: `maphra@${require('../../package.json').version}`,
      },
      runtime: {
        name: 'browser',
        version: navigator.userAgent,
      },
      device: {
        memory: performance.memory ? Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) : null,
        online: navigator.onLine,
        language: navigator.language,
      },
    };
    
    return event;
  },
  
  // Before breadcrumb hook - modify or filter breadcrumbs
  beforeBreadcrumb(breadcrumb, hint) {
    // Don't log console.log breadcrumbs in production
    if (isProduction && breadcrumb.category === 'console' && breadcrumb.level === 'log') {
      return null;
    }
    
    // Sanitize sensitive data from breadcrumbs
    if (breadcrumb.data) {
      // Remove tokens from URLs
      if (breadcrumb.data.url) {
        breadcrumb.data.url = breadcrumb.data.url.replace(/token=[^&]+/, 'token=[FILTERED]');
      }
      
      // Remove sensitive headers
      if (breadcrumb.data.headers) {
        const sensitiveHeaders = ['authorization', 'x-api-key', 'cookie'];
        sensitiveHeaders.forEach(header => {
          if (breadcrumb.data.headers[header]) {
            breadcrumb.data.headers[header] = '[FILTERED]';
          }
        });
      }
    }
    
    return breadcrumb;
  },
};

/**
 * Initialize Sentry
 * Call this function as early as possible in your app
 */
export const initializeSentry = () => {
  try {
    Sentry.init(sentryConfig);
    console.log('[Sentry] Initialized successfully:', {
      environment: sentryConfig.environment,
      release: sentryConfig.release,
      tracesSampleRate: sentryConfig.tracesSampleRate,
      replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
    });
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
};

/**
 * Manually capture an exception
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  Sentry.captureException(error, {
    tags: context.tags,
    extra: context.extra,
    level: context.level || 'error',
  });
};

/**
 * Manually capture a message
 * @param {string} message - The message to capture
 * @param {string} level - The severity level
 */
export const captureMessage = (message, level = 'info') => {
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 * @param {Object} user - User information
 */
export const setUser = (user) => {
  Sentry.setUser(user);
};

/**
 * Clear user context
 */
export const clearUser = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb manually
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Set custom tag
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

/**
 * Set custom context
 * @param {string} name - Context name
 * @param {Object} context - Context data
 */
export const setContext = (name, context) => {
  Sentry.setContext(name, context);
};

/**
 * Show user feedback dialog
 * Allows users to provide feedback when they encounter errors
 */
export const showUserFeedback = () => {
  try {
    const eventId = Sentry.lastEventId();
    if (eventId) {
      Sentry.showReportDialog({
        eventId,
        title: 'It looks like we\'re having issues.',
        subtitle: 'Our team has been notified.',
        subtitle2: 'If you\'d like to help, tell us what happened below.',
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelClose: 'Close',
        labelSubmit: 'Submit',
        errorGeneric: 'An error occurred while submitting your report. Please try again.',
        errorFormEntry: 'Some fields were invalid. Please correct the errors and try again.',
        successMessage: 'Your feedback has been sent. Thank you!',
      });
    }
  } catch (error) {
    console.error('[Sentry] Failed to show feedback dialog:', error);
  }
};

/**
 * Start a new session manually
 */
export const startSession = () => {
  try {
    Sentry.startSession();
  } catch (error) {
    console.error('[Sentry] Failed to start session:', error);
  }
};

/**
 * End current session manually
 */
export const endSession = () => {
  try {
    Sentry.endSession();
  } catch (error) {
    console.error('[Sentry] Failed to end session:', error);
  }
};

/**
 * Get current Sentry release version
 */
export const getRelease = () => {
  return `maphra@${require('../../package.json').version}`;
};

/**
 * Get current Sentry environment
 */
export const getEnvironment = () => {
  return process.env.NODE_ENV || 'development';
};

// Export Sentry instance for direct access
export { Sentry };

// Export default configuration
export default sentryConfig;

